import React, { useState, useEffect, createContext, useContext } from 'react';
import {
  getCurrentUser, onAuthChange, logoutUser, attachTenantToCurrentUser,
  updateCurrentUserProfile, deleteCurrentAccount, slugify, authPending,
} from './auth';
import { resetTenant, detachTenant } from './db';
import { firebaseEnabled } from '../firebase/config';
import { callFunction } from '../firebase/functions';
import { isTrialExpired } from '../data/trial';

const TenantContext = createContext(null);

export function TenantProvider({ children }) {
  // undefined = caricamento iniziale, null = non autenticato, oggetto = utente
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const unsub = onAuthChange((u) => setUser(u || null));
    // Allineamento iniziale: se c'è già un utente noto usalo; se il backend
    // (Firebase) sta ancora ripristinando la sessione resta in `undefined`
    // (loading) invece di segnare "non autenticato".
    const initial = getCurrentUser();
    if (initial) setUser(initial);
    else if (!authPending()) setUser(null);
    return unsub;
  }, []);

  const activeTenantId = user?.tenantId || null;

  // Profilo utente derivato (allineato all'oggetto user dell'auth locale).
  const userProfile = user
    ? {
        displayName: user.displayName, nome: user.nome, cognome: user.cognome,
        phone: user.phone, email: user.email, role: user.role,
        taxInfo: user.taxInfo || {}, notificationPrefs: user.notificationPrefs || {},
        activeTenantId: user.tenantId, tenantName: user.tenantName,
      }
    : null;

  // Crea il tenant (workspace) per l'utente corrente.
  // Signature compatibile con la pagina register: (nome, indirizzoSede, partitaIva, coords?).
  // I dati aziendali della registrazione popolano i Dati di fatturazione del profilo.
  async function createTenant(nome, indirizzoSede = '', partitaIva = '', coords = null) {
    const current = getCurrentUser();
    if (!current) throw new Error('Non autenticato');
    // tenantId UNIVOCO: slug leggibile + uid dell'owner. Impedisce che due aziende
    // con lo stesso nome ottengano lo stesso tenantId e finiscano per condividere i
    // dati (isolamento multi-tenant). Il tenant è 1:1 con l'utente che lo crea.
    const slug = slugify(nome);
    const tenantId = slug ? `${slug}-${current.uid}` : current.uid;
    // NB: niente ensureTenant qui. In registrazione l'email non è ancora verificata
    // e le regole negano i dati del tenant: attaccare gli onSnapshot ora fallirebbe
    // e bloccherebbe il re-attach. I listener partono da AppDataProvider dopo la
    // verifica (gate su emailVerified). Qui scriviamo solo il profilo utente.
    await attachTenantToCurrentUser(tenantId, nome);
    // Il workspace nasce VUOTO in ogni ambiente (locale e hosting): i dati reali
    // li aggiunge l'utente (form o Import Excel). Nessun dato demo generato.
    // Precompila i dati di fatturazione dal form di registrazione.
    updateCurrentUserProfile({ taxInfo: { ragioneSociale: nome, partitaIva, indirizzo: indirizzoSede } });
    return tenantId;
  }

  async function logout() {
    const tid = getCurrentUser()?.tenantId;
    await logoutUser();
    if (tid) detachTenant(tid); // stacca i listener onSnapshot del tenant
  }

  function updateProfile(patch) {
    updateCurrentUserProfile(patch);
  }

  // Elimina account. Su Firebase passa dalla Cloud Function `deleteAccount`
  // (Admin SDK): cancella dati tenant + doc utente + utente Auth in modo
  // affidabile (niente auth/requires-recent-login). In locale, equivalente client.
  async function deleteAccount() {
    if (firebaseEnabled()) {
      await callFunction('deleteAccount', {});
      await logoutUser().catch(() => {}); // chiude la sessione lato client
      return;
    }
    const tid = getCurrentUser()?.tenantId;
    if (tid) resetTenant(tid);
    deleteCurrentAccount();
  }

  const value = {
    user,
    uid: user?.uid || null,
    userProfile,
    activeTenantId,
    loading: user === undefined,
    isAuthenticated: !!user,
    emailVerified: user?.emailVerified ?? false,
    // Periodo di prova: scadenza (ms) e flag "scaduto". null = nessun trial.
    trialEndsAt: user?.trialEndsAt ?? null,
    trialExpired: isTrialExpired(user?.trialEndsAt ?? null),
    createTenant,
    logout,
    updateProfile,
    deleteAccount,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used inside <TenantProvider>');
  return ctx;
}
